/**
 * useFeedbackMode — Shared feedback state + executeNode wrapper
 *
 * Manages feedbackMode toggle, feedbackText state, and a handleRefine
 * callback that writes in_feedback to YDoc and calls executeNode.
 *
 * V2 migration: Uses V2 CanvasContext imports. Self-contained — no
 * imports from old canvas/ directory.
 */

import { useState, useCallback } from 'react'
import { useReactFlow } from 'reactflow'
import { executeNode, InsufficientSparksError } from '../../../api/client'
import { useProjectId } from '../../../contexts/ProjectIdContext'
import { useActiveCanvasId } from '../../../contexts/ActiveCanvasIdContext'
import { useShowInsufficientSparks } from '../../../contexts/InsufficientSparksContext'
import { useCanvasOptional } from '../CanvasContext'
import { createNadDebug } from '../../../utils/debugLog'
import { modelSourceOf } from '../../../analytics/nodeAnalytics'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../hooks/useYCanvas'

const refineVariantCountLog = createNadDebug('dw:refine-variant-count')

interface UseFeedbackModeOptions {
  nodeId: string
  nodeType: 'characterDesigner' | 'locationDesigner' | 'objectDesigner'
  status: string | undefined
}

export interface RefineRunOptions {
  /**
   * Project-relative path of a freshly baked Refine-tab annotation.
   *
   * Sent as a per-run `state_updates` argument, which `execute-node` freezes
   * into a detached override that execution consumes — so it is a transient
   * action argument, not durable node data. The agent uses it as the edit
   * target in place of the clean picked variant.
   *
   * ALWAYS sent on a refine, empty string included: the field is thereby
   * self-correcting, and a drawing from a previous run can never leak into a
   * later text-only refine.
   */
  annotatedImagePath?: string
}

interface UseFeedbackModeReturn {
  feedbackMode: boolean
  feedbackText: string
  setFeedbackText: (text: string) => void
  toggleFeedbackMode: () => void
  handleRefine: (retryCount?: number, options?: RefineRunOptions) => Promise<void>
}

/**
 * Shared hook for feedback/refinement mode across designer panels.
 *
 * Manages feedbackMode + feedbackText state and provides a handleRefine
 * callback that writes in_feedback to YDoc, then calls executeNode.
 * All node data is already in YDoc — no config parameter needed.
 *
 * Guards:
 * - Returns early if feedbackText is empty or status is 'running'
 * - Sets error state if projectId is missing (no silent failure)
 */
export function useFeedbackMode({
  nodeId,
  nodeType,
  status,
}: UseFeedbackModeOptions): UseFeedbackModeReturn {
  const projectId = useProjectId()
  const canvasId = useActiveCanvasId()
  const { setNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const showInsufficientSparks = useShowInsufficientSparks()
  const [feedbackMode, setFeedbackMode] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')

  const toggleFeedbackMode = useCallback(() => {
    setFeedbackMode((prev) => !prev)
  }, [])

  const handleRefine = useCallback(
    async (retryCount: number = 1, options?: RefineRunOptions) => {
      if (status === 'running' || !feedbackText.trim()) return

      refineVariantCountLog({
        event: 'refine-handler',
        nodeType,
        node_id: nodeId,
        retryCount,
      })

      if (!projectId) {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
              : n
          )
        )
        return
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n
        )
      )

      try {
        // Write feedback to YDoc before executing
        canvas?.updateNodeData({ id: nodeId, type: nodeType }, { in_feedback: feedbackText.trim() })
        // Forward the selected variant count so a "4x" refine fans out to N
        // generations instead of being dropped (backend defaults count to 1).
        await executeNode(projectId, nodeId, {
          count: retryCount,
          canvasId,
          stateUpdates: { annotated_image_path: options?.annotatedImagePath ?? '' },
          analytics: {
            nodeType,
            tab: 'refine',
            generationMode: 'refine',
            modelSource: modelSourceOf(canvas?.getYNode?.(nodeId)?.get('in_model')),
          },
        })
        setFeedbackText('')
        setFeedbackMode(false)
      } catch (err) {
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({
            balance: err.balance,
            required: err.required,
            nodeId,
            nodeType,
            projectId,
          })
          // Revert optimistic 'running' status so the card doesn't look stuck.
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    },
    [projectId, canvasId, nodeId, nodeType, status, setNodes, feedbackText, canvas, showInsufficientSparks]
  )

  return { feedbackMode, feedbackText, setFeedbackText, toggleFeedbackMode, handleRefine }
}
